import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { API } from '../App';
import { Plane, Filter, RefreshCw, Activity, X, Loader, Sparkles, ExternalLink, MapPin, Download, Clock } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom marker icons for news events
const createEventIcon = (threatLevel) => {
  const colors = {
    'Critical': '#FF3B30',
    'High': '#FFD60A', 
    'Medium': '#00F0FF',
    'Low': '#00FF9D'
  };
  const color = colors[threatLevel] || colors['Medium'];
  
  return L.divIcon({
    className: 'news-event-marker',
    html: `<div style="
      background-color: ${color}; 
      width: 14px; 
      height: 14px; 
      border-radius: 50%; 
      border: 2px solid white; 
      box-shadow: 0 0 12px ${color}, 0 0 20px ${color}40;
      animation: pulse 2s infinite;
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
};

// Aircraft icon with rotation
const createAircraftIcon = (heading, aircraftType) => {
  const color = aircraftType === 'Military' ? '#FF3B30' : '#00F0FF';
  return L.divIcon({
    className: 'aircraft-marker',
    html: `<div style="
      font-size: 18px; 
      transform: rotate(${heading}deg);
      filter: drop-shadow(0 0 4px ${color});
    ">✈️</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

function MapUpdater({ aircraft }) {
  const map = useMap();
  
  useEffect(() => {
    if (aircraft.length > 0 && map && map.getContainer()) {
      try {
        const bounds = aircraft.map(a => [a.latitude, a.longitude]);
        if (bounds.length > 0) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (e) {
        console.warn('MapUpdater: Map not ready');
      }
    }
  }, [aircraft, map]);
  
  return null;
}

export default function GlobeView({ aircraftOnly = false }) {
  const [showAircraft, setShowAircraft] = useState(aircraftOnly);
  const [aircraft, setAircraft] = useState([]);
  const [newsEvents, setNewsEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [aiSummary, setAiSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filters, setFilters] = useState({
    type: 'All',
    minAltitude: 0,
    maxAltitude: 15000,
  });

  useEffect(() => {
    // If aircraftOnly prop changes, update state
    setShowAircraft(aircraftOnly);
  }, [aircraftOnly]);

  // Auto-refresh countdown
  useEffect(() => {
    if (!autoRefresh) return;
    const countdownInterval = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 30);
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, [autoRefresh]);

  useEffect(() => {
    if (!showAircraft) {
      loadNewsEvents();
      if (autoRefresh) {
        const interval = setInterval(loadNewsEvents, 30000);
        return () => clearInterval(interval);
      }
    }
    if (showAircraft) {
      loadAircraft();
      if (autoRefresh) {
        const interval = setInterval(loadAircraft, 30000);
        return () => clearInterval(interval);
      }
    }
  }, [showAircraft, autoRefresh]);

  const loadNewsEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news/events`);
      setNewsEvents(response.data.events || []);
      setLastUpdate(new Date());
      setCountdown(30);
    } catch (error) {
      console.error('Error loading news events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAircraft = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/aircraft/live`);
      if (response.data.aircraft) {
        setAircraft(response.data.aircraft.filter(a => {
          const altitude = a.altitude || 0;
          if (filters.type !== 'All' && a.aircraft_type !== filters.type) return false;
          return altitude >= filters.minAltitude && altitude <= filters.maxAltitude;
        }));
        setLastUpdate(new Date());
        setCountdown(30);
      }
    } catch (error) {
      console.error('Error loading aircraft:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = async (event) => {
    setSelectedEvent(event);
    setSelectedAircraft(null);
    setAiSummary('');
    setLoadingSummary(true);
    
    try {
      const response = await axios.post(`${API}/news/quick-summary`, {
        title: event.title,
        summary: event.summary,
        region: event.region
      });
      setAiSummary(response.data.ai_summary);
    } catch (error) {
      console.error('Error generating AI summary:', error);
      setAiSummary('Unable to generate AI briefing. Please view the full article.');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleAircraftClick = (ac) => {
    setSelectedAircraft(ac);
    setSelectedEvent(null);
  };

  const closePanel = () => {
    setSelectedEvent(null);
    setSelectedAircraft(null);
    setAiSummary('');
  };

  // Download functions
  const downloadData = () => {
    const dataToDownload = showAircraft ? aircraft : newsEvents;
    const dataStr = JSON.stringify(dataToDownload, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    const filename = showAircraft 
      ? `DefenseWatch_Aircraft_${new Date().toISOString().split('T')[0]}.json`
      : `DefenseWatch_NewsEvents_${new Date().toISOString().split('T')[0]}.json`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const getThreatColor = (level) => {
    switch (level) {
      case 'Critical': return 'text-[#FF3B30] bg-[#FF3B30]/10 border-[#FF3B30]/30';
      case 'High': return 'text-[#FFD60A] bg-[#FFD60A]/10 border-[#FFD60A]/30';
      case 'Medium': return 'text-[#00F0FF] bg-[#00F0FF]/10 border-[#00F0FF]/30';
      default: return 'text-[#00FF9D] bg-[#00FF9D]/10 border-[#00FF9D]/30';
    }
  };

  return (
    <div className="relative w-full h-full" data-testid="globe-view">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-[1000] glass-panel border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>GLOBAL INTELLIGENCE MAP</h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              {showAircraft ? 'Real-time aircraft tracking' : 'Click event markers for AI briefings'} 
              • Auto-refresh: {autoRefresh ? `${countdown}s` : 'OFF'}
              {lastUpdate && ` • Updated: ${lastUpdate.toLocaleTimeString()}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                autoRefresh ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50' : 'bg-white/5 text-zinc-400 border border-white/10'
              }`}
              title={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            >
              <Clock className="w-4 h-4" />
              <span>{autoRefresh ? '30s' : 'OFF'}</span>
            </button>
            <button
              data-testid="toggle-aircraft-button"
              onClick={() => setShowAircraft(!showAircraft)}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                showAircraft ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50' : 'bg-white/5 text-zinc-400 border border-white/10'
              }`}
            >
              <Plane className="w-4 h-4" />
              <span>Aircraft {showAircraft && `(${aircraft.length})`}</span>
            </button>
            {showAircraft && (
              <button
                data-testid="filter-aircraft-button"
                onClick={() => setFilterOpen(!filterOpen)}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                  filterOpen ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50' : 'bg-white/5 text-zinc-400 border border-white/10'
                }`}
              >
                <Filter className="w-4 h-4" />
              </button>
            )}
            <button
              data-testid="download-data-button"
              onClick={downloadData}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10 transition-all"
              title="Download data for offline use"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              data-testid="refresh-map-button"
              onClick={() => showAircraft ? loadAircraft() : loadNewsEvents()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-[#00FF9D]/10 border border-[#00FF9D]/30">
              <Activity className="w-4 h-4 text-[#00FF9D]" />
              <span className="text-xs font-mono text-[#00FF9D]">LIVE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {filterOpen && showAircraft && (
        <div className="absolute top-24 right-4 z-[1000] w-80 glass-panel tactical-border p-4">
          <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Aircraft Filters</h3>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Type</label>
              <select 
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono"
              >
                <option value="All">All</option>
                <option value="Civilian">Civilian</option>
                <option value="Military">Military</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Altitude Range (m)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={filters.minAltitude}
                  onChange={(e) => setFilters({...filters, minAltitude: parseInt(e.target.value) || 0})}
                  className="w-1/2 bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono"
                  placeholder="Min"
                />
                <input
                  type="number"
                  value={filters.maxAltitude}
                  onChange={(e) => setFilters({...filters, maxAltitude: parseInt(e.target.value) || 15000})}
                  className="w-1/2 bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono"
                  placeholder="Max"
                />
              </div>
            </div>
            <button
              onClick={() => { loadAircraft(); setFilterOpen(false); }}
              className="w-full btn-primary py-2"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* News Event Detail Panel */}
      {selectedEvent && (
        <div className="absolute top-24 left-4 z-[1000] w-96 max-h-[calc(100vh-120px)] overflow-y-auto glass-panel tactical-border p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#00FF9D]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed' }}>INTELLIGENCE BRIEFING</h2>
            </div>
            <button onClick={closePanel} className="text-zinc-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Event Info */}
          <div className="space-y-4">
            <div className="p-3 bg-black/20 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-1 text-[10px] font-bold uppercase border ${getThreatColor(selectedEvent.threat_level)}`}>
                  {selectedEvent.threat_level}
                </span>
                <span className="text-[10px] text-zinc-500 font-mono">{selectedEvent.source}</span>
              </div>
              <h3 className="text-sm font-bold text-white mb-2">{selectedEvent.title}</h3>
              <p className="text-xs text-zinc-400 mb-2">{selectedEvent.summary}</p>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500 font-mono">
                <span>📍 {selectedEvent.city}, {selectedEvent.region}</span>
              </div>
            </div>

            {/* AI Summary */}
            <div className="p-3 bg-[#00FF9D]/5 border border-[#00FF9D]/20">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#00FF9D]" />
                <h4 className="text-xs font-bold uppercase text-[#00FF9D]">AI Intelligence Briefing</h4>
              </div>
              {loadingSummary ? (
                <div className="flex items-center gap-2 text-zinc-400">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="text-xs font-mono">Generating briefing...</span>
                </div>
              ) : (
                <p className="text-xs text-zinc-300 leading-relaxed whitespace-pre-line">
                  {aiSummary || 'Click to generate AI briefing'}
                </p>
              )}
            </div>

            {/* Action Button */}
            <a
              href={selectedEvent.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 btn-primary"
            >
              <ExternalLink className="w-4 h-4" />
              <span>VIEW FULL ARTICLE</span>
            </a>
          </div>
        </div>
      )}

      {/* Aircraft Detail Panel */}
      {selectedAircraft && (
        <div className="absolute top-24 left-4 z-[1000] w-96 glass-panel tactical-border p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-[#00FF9D]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed' }}>AIRCRAFT DETAILS</h2>
            </div>
            <button onClick={closePanel} className="text-zinc-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Aircraft Type & Country */}
            <div className="p-3 bg-black/20 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">{selectedAircraft.country_flag}</span>
                  <div>
                    <h3 className="text-lg font-bold text-white">{selectedAircraft.callsign}</h3>
                    <p className="text-xs text-zinc-400">{selectedAircraft.origin_country}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 text-xs font-bold uppercase ${
                  selectedAircraft.aircraft_type === 'Military' 
                    ? 'bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30' 
                    : 'bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30'
                }`}>
                  {selectedAircraft.aircraft_type}
                </span>
              </div>
            </div>

            {/* Flight Route */}
            <div className="p-3 bg-black/20 border border-white/10">
              <h4 className="text-xs font-bold uppercase mb-3 text-[#00FF9D]">Flight Route</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-[#00FF9D]"></div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Departure</p>
                    <p className="text-sm text-white font-mono">{selectedAircraft.departure}</p>
                  </div>
                </div>
                <div className="border-l-2 border-dashed border-zinc-600 ml-1.5 h-4"></div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-[#FF3B30]"></div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Arrival</p>
                    <p className="text-sm text-white font-mono">{selectedAircraft.arrival}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Flight Data */}
            <div className="p-3 bg-black/20 border border-white/10">
              <h4 className="text-xs font-bold uppercase mb-3 text-[#00FF9D]">Flight Data</h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-zinc-500">Altitude</p>
                  <p className="text-white font-mono">{Math.round(selectedAircraft.altitude).toLocaleString()}m</p>
                </div>
                <div>
                  <p className="text-zinc-500">Speed</p>
                  <p className="text-white font-mono">{Math.round(selectedAircraft.velocity)} m/s</p>
                </div>
                <div>
                  <p className="text-zinc-500">Heading</p>
                  <p className="text-white font-mono">{Math.round(selectedAircraft.heading)}°</p>
                </div>
                <div>
                  <p className="text-zinc-500">Phase</p>
                  <p className={`font-mono ${
                    selectedAircraft.flight_phase === 'Cruising' ? 'text-[#00FF9D]' :
                    selectedAircraft.flight_phase === 'Climbing' ? 'text-[#FFD60A]' :
                    selectedAircraft.flight_phase === 'Descending' ? 'text-[#00F0FF]' :
                    'text-zinc-400'
                  }`}>{selectedAircraft.flight_phase}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Position</p>
                  <p className="text-white font-mono text-[10px]">
                    {selectedAircraft.latitude.toFixed(4)}°, {selectedAircraft.longitude.toFixed(4)}°
                  </p>
                </div>
                <div>
                  <p className="text-zinc-500">ICAO24</p>
                  <p className="text-white font-mono">{selectedAircraft.icao24}</p>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className={`p-3 text-center ${
              selectedAircraft.on_ground 
                ? 'bg-zinc-700/20 border border-zinc-600' 
                : 'bg-[#00FF9D]/10 border border-[#00FF9D]/30'
            }`}>
              <p className={`text-sm font-bold uppercase ${selectedAircraft.on_ground ? 'text-zinc-400' : 'text-[#00FF9D]'}`}>
                {selectedAircraft.on_ground ? '🛬 ON GROUND' : '✈️ IN FLIGHT'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="w-full h-full pt-24">
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
          className="leaflet-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          
          {/* News Event Markers */}
          {!showAircraft && newsEvents.map((event) => (
            <Marker
              key={event.id}
              position={[event.lat, event.lng]}
              icon={createEventIcon(event.threat_level)}
              eventHandlers={{
                click: () => handleEventClick(event)
              }}
            >
              <Popup>
                <div className="text-xs max-w-[200px]">
                  <p className="font-bold text-white">{event.title}</p>
                  <p className="text-zinc-400 mt-1">Click for AI briefing</p>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Aircraft Markers */}
          {showAircraft && aircraft.map((ac, idx) => (
            <Marker
              key={idx}
              position={[ac.latitude, ac.longitude]}
              icon={createAircraftIcon(ac.heading, ac.aircraft_type)}
              eventHandlers={{
                click: () => handleAircraftClick(ac)
              }}
            >
              <Popup>
                <div className="text-xs font-mono">
                  <p className="font-bold">{ac.country_flag} {ac.callsign}</p>
                  <p className={ac.aircraft_type === 'Military' ? 'text-red-400' : 'text-cyan-400'}>
                    {ac.aircraft_type}
                  </p>
                  <p className="text-zinc-400">Click for details</p>
                </div>
              </Popup>
            </Marker>
          ))}
          
          {showAircraft && <MapUpdater aircraft={aircraft} />}
        </MapContainer>
      </div>

      {/* Legend */}
      {!showAircraft && (
        <div className="absolute bottom-4 right-4 z-[1000] glass-panel p-3">
          <h4 className="text-[10px] text-zinc-400 uppercase tracking-wider mb-2">Threat Level</h4>
          <div className="space-y-1">
            {[
              { level: 'Critical', color: '#FF3B30' },
              { level: 'High', color: '#FFD60A' },
              { level: 'Medium', color: '#00F0FF' },
              { level: 'Low', color: '#00FF9D' }
            ].map(item => (
              <div key={item.level} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-[10px] text-zinc-400">{item.level}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAircraft && (
        <div className="absolute bottom-4 right-4 z-[1000] glass-panel p-3">
          <h4 className="text-[10px] text-zinc-400 uppercase tracking-wider mb-2">Aircraft Type</h4>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm">✈️</span>
              <span className="text-[10px] text-[#00F0FF]">Civilian</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">✈️</span>
              <span className="text-[10px] text-[#FF3B30]">Military</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
